/**
 * People templates — 3 fill-in-the-blank documents for non-employee engagements.
 *
 * These three cover the same commercial ground from three different angles, and a founder should
 * be able to tell which one they are in:
 *   independent-contractor-agreement    — someone does defined work for a fee and hands over output
 *   consultant-agreement                — someone gives advice and access to judgement, on a retainer
 *   contractor-work-for-hire-agreement  — someone is commissioned to create, and the brand must own it
 *
 * Nothing in this file addresses whether a worker should be an employee instead. That question turns
 * on how the engagement actually operates day to day, and it belongs with counsel before a pattern is
 * set across a market — not in a clause. The templates state the parties' intent and stop there.
 *
 * Every body uses visible [BRACKET] blanks. An unfilled blank stays on the page on purpose.
 * The renderer prepends the standard disclaimer; nothing here competes with it.
 */

export const PEOPLE_TEMPLATES = [
  {
    id: "independent-contractor-agreement",
    docId: "independent-contractor-agreement",
    title: "INDEPENDENT CONTRACTOR AGREEMENT",
    version: "1.0",
    lastReviewed: "2026-08-25",

    founderGuide: {
      useThisWhen:
        "Someone who is not on your payroll is about to do defined work for a fee: a demo lead, a field marketer, a bookkeeper, a warehouse hand, a fractional operator, a designer on a one-off brief. Sign it before the first shift or the first file, and if they have already started, sign it now rather than at the next invoice.",
      whyItMatters:
        "Two things go wrong and both are expensive. The first is ownership — paying an invoice does not move rights, so the contractor who designed your label, shot your product or wrote your copy keeps it until they assign it in writing, and you find out during diligence or at the printer. The second is classification, and this is the part no document solves: whether someone is an employee or a contractor is decided by how the engagement actually operates, not by what the paper calls it. If you set the hours, require exclusivity and supervise the daily work, the paper saying 'independent contractor' does not change the pattern. This template records what the parties intend and deliberately stops there. Put the actual working pattern — not just this document — to counsel before you run the arrangement across a market or a roster.",
      negotiateCarefully: [
        {
          point: "The paper and the working pattern have to match",
          detail:
            "The clause calling someone a contractor is the least important part of this document. What matters is who controls the method, who sets the schedule, whether the person works for others, and whose tools and premises are used. If you need set hours, exclusivity and daily direction, say so out loud and take the arrangement to counsel before you scale it, rather than papering over the gap.",
        },
        {
          point: "Present assignment, not a promise to assign",
          detail:
            "An assignment that operates as work is created leaves no gap if the contractor goes quiet, disputes an invoice or simply disappears. A promise to sign something later is only as good as your ability to find them. Pair it with a further-assurances obligation and delivery of source and working files, or you will own an export you cannot edit.",
        },
        {
          point: "Carve out what they already owned",
          detail:
            "Contractors arrive with templates, presets, code libraries and process documents built over years, and a blanket assignment sweeps those in. List them on the prior-materials schedule and take a broad licence to whatever is embedded in your deliverables instead. A contractor who will not sign because the assignment reaches their entire toolkit is not being unreasonable.",
        },
        {
          point: "Third-party material inside the deliverable",
          detail:
            "Fonts, stock imagery, music and plug-ins come with their own licences, usually held by the contractor and usually scoped for comps rather than mass-reproduced packaging or paid media. Require the list up front and a warranty that the licences reach the uses you actually intend, because the shortfall surfaces at pre-press.",
        },
        {
          point: "Insurance for anyone working in someone else's building",
          detail:
            "Demo staff, merchandisers, event crews and warehouse contractors work inside retailers, distributors and 3PLs, and those sites usually require coverage the contractor does not carry. Get certificates before the first shift, not after an incident, because the account relationship is what you lose.",
        },
      ],
      redFlags: [
        "No assignment of work product at all, or ownership that only transfers once the final invoice clears.",
        "An engagement that sets fixed hours, requires exclusivity and supervises daily work, papered as a contractor arrangement.",
        "Deliverables built on third-party fonts, stock or music with no licence disclosure and no pass-through.",
        "No termination right, or a cancellation charge equal to the full project fee.",
        "On-site, warehouse or event work with no insurance requirement and no certificate.",
        "No confidentiality obligation where the contractor will see formulations, supplier pricing or buyer lists.",
      ],
      timeToComplete: "20 minutes to fill · same-day to sign",
    },

    placeholders: [
      { token: "[EFFECTIVE DATE]", label: "Effective date", example: "September 1, 2026" },
      { token: "[COMPANY NAME]", label: "Your legal entity name", brandKey: "legalEntityName", example: "Acme Beverage Co., LLC" },
      { token: "[COMPANY STATE]", label: "Your state of formation", brandKey: "stateOfFormation", example: "Delaware" },
      { token: "[COMPANY ENTITY TYPE]", label: "Your entity type", example: "limited liability company" },
      { token: "[COMPANY ADDRESS]", label: "Your address", brandKey: "principalAddress", example: "123 Main St, Austin, TX 78701" },
      { token: "[BRAND NAME]", label: "Your brand name", brandKey: "tradeName", example: "Acme" },
      { token: "[CONTRACTOR NAME]", label: "Contractor's legal name", example: "Dana Reyes / Reyes Field Marketing LLC" },
      { token: "[CONTRACTOR ADDRESS]", label: "Contractor's address", example: "88 Cedar St, Austin, TX 78702" },
      { token: "[SERVICES SUMMARY]", label: "One line describing the services", example: "in-store demonstration and field marketing services in the Central Texas market" },
      { token: "[SCOPE OF WORK]", label: "Scope of work (Schedule A)", example: "12 in-store demo days, 4 hours each, per the demo playbook; setup, sampling, post-event report" },
      { token: "[DELIVERABLES]", label: "Deliverables and what done means", example: "one post-event report per demo with photos, sample counts and buyer feedback, in the Company's template" },
      { token: "[ACCEPTANCE PERIOD]", label: "Time the Company has to accept or reject", example: "5 business days" },
      { token: "[FEE]", label: "Fee and how it is calculated", example: "$450 per completed demo day, or $85 per hour for approved additional work" },
      { token: "[INVOICE FREQUENCY]", label: "How often the contractor invoices", example: "monthly, in arrears" },
      { token: "[PAYMENT PERIOD]", label: "Time to pay a correct invoice", example: "30 days from receipt" },
      { token: "[EXPENSE APPROVAL THRESHOLD]", label: "Expense amount needing prior approval", example: "$250 per item" },
      { token: "[TERM END DATE]", label: "Date the engagement ends unless extended", example: "December 31, 2026" },
      { token: "[TERMINATION NOTICE PERIOD]", label: "Notice either side gives to end it", example: "10 days" },
      { token: "[CONFIDENTIALITY PERIOD]", label: "How long confidentiality runs after the work ends", example: "3 years" },
      { token: "[PRIOR MATERIALS]", label: "Contractor's pre-existing materials (Schedule B)", example: "Reyes demo-report template; Lightroom presets; event checklist" },
      { token: "[THIRD PARTY MATERIALS]", label: "Third-party fonts, stock and assets (Schedule C)", example: "Stock photo licence held by Contractor — standard licence, no packaging use" },
      { token: "[INSURANCE COVERAGE]", label: "Insurance the contractor must carry", example: "commercial general liability at the limits required by the retailer or 3PL site" },
      { token: "[GOVERNING LAW STATE]", label: "Governing law state", example: "Delaware" },
      { token: "[VENUE]", label: "Venue for disputes", example: "Wilmington, Delaware" },
    ],

    body: `This Independent Contractor Agreement (this "Agreement") is entered into as of [EFFECTIVE DATE]
(the "Effective Date") by and between:

  [COMPANY NAME], a [COMPANY STATE] [COMPANY ENTITY TYPE], with a principal place of
  business at [COMPANY ADDRESS], which markets products under the brand [BRAND NAME]
  (the "Company"); and

  [CONTRACTOR NAME], with an address at [CONTRACTOR ADDRESS] (the "Contractor").

1. ENGAGEMENT
The Company engages the Contractor to provide [SERVICES SUMMARY] (the "Services"), and the
Contractor accepts the engagement, on the terms of this Agreement. The Services are described in
Schedule A. This Agreement does not commit the Company to request any particular volume of Services
and does not commit the Contractor to accept work outside Schedule A.

2. SCOPE OF WORK
Schedule A sets out the Services, the deliverables, the timing and the location or territory in
which the Services are performed. The parties may add or replace a Schedule A by signing a new one;
a signed Schedule A takes effect on its own terms and is governed by this Agreement. Where a
Schedule A conflicts with this Agreement, this Agreement controls on ownership, confidentiality,
third-party material and insurance, and Schedule A controls on scope, timing and fees.

The Contractor shall perform the Services with the skill and care reasonably expected of a
professional in the Contractor's field, in accordance with the Company's written brand, quality and
site requirements, and in compliance with the requirements that apply to the Contractor's own
business and to any site at which the Services are performed.

3. DELIVERABLES AND ACCEPTANCE
The Contractor shall deliver the items described as [DELIVERABLES] in Schedule A (the
"Deliverables") by the dates stated there, in the formats stated there, including source and
working files where the Deliverable is a design, artwork, photograph, video, document or code.

The Company shall review each Deliverable within [ACCEPTANCE PERIOD] of receiving it and shall
either accept it or give written notice of what does not meet Schedule A. The Contractor shall
correct the items identified and resubmit within a period the parties agree in writing. A
Deliverable the Company does not respond to within [ACCEPTANCE PERIOD] is treated as accepted.
Acceptance is not a waiver of any warranty in Section 12 and does not limit the Company's rights if
a defect appears later.

4. FEES AND INVOICING
The Company shall pay the Contractor [FEE]. Unless Schedule A says otherwise, that amount is the
Contractor's entire compensation for the Services and covers the Contractor's own equipment,
software, workspace, mileage, phone and administrative costs.

The Contractor shall invoice [INVOICE FREQUENCY]. Each invoice shall identify the Services
performed, the dates, the hours or units where the fee is calculated on them, and any approved
expenses with receipts attached. The Company shall pay each correct and undisputed invoice within
[PAYMENT PERIOD]. If the Company disputes part of an invoice it shall pay the undisputed part on
time, notify the Contractor promptly of the disputed part, and the parties shall resolve it in good
faith. The Contractor is responsible for its own taxes, filings and registrations arising from the
fees, and the Company does not withhold from them unless it is required to.

5. EXPENSES
The Company reimburses only expenses that are reasonable, incurred directly for the Services, and
supported by receipts. Any single expense above [EXPENSE APPROVAL THRESHOLD], and any travel,
freight, sample purchase, venue cost, subcontractor cost or third-party licence, requires the
Company's prior written approval. Unapproved expenses are the Contractor's own cost. Expenses are
reimbursed at cost, without markup, unless Schedule A states an agreed markup.

6. TERM AND TERMINATION
This Agreement begins on the Effective Date and continues until [TERM END DATE] unless extended in
writing or ended earlier under this Section.

Either party may end this Agreement, or any Schedule A, for any reason on
[TERMINATION NOTICE PERIOD] written notice. Either party may end it immediately on written notice if
the other materially breaches and does not cure the breach within [TERMINATION NOTICE PERIOD] of
written notice describing it. The Company may end it immediately if the Contractor's performance
would breach a site, retailer or customer requirement, or if the Contractor loses any licence,
certification or insurance required for the Services.

On termination the Company shall pay for Services properly performed and Deliverables properly
delivered up to the termination date, and for approved expenses already incurred. Neither party owes
the other a cancellation charge or the balance of an unperformed fee. The Contractor shall promptly
deliver all work in progress in its then-current form, together with the files described in
Section 8, and shall return or destroy the Company's property and Confidential Information under
Section 7. Sections 7, 8, 9, 11, 12, 14 and 15 survive termination.

7. CONFIDENTIALITY
"Confidential Information" means the Company's non-public information in any form, whether or not
marked confidential, including: formulations, recipes, specifications and process parameters;
ingredient identities, sources and costs; co-manufacturer, supplier and distributor identities and
terms; landed cost, margin, pricing and promotional plans; retailer and buyer contacts, feedback and
pipeline; forecasts, sales data and financial information; unreleased packaging, artwork, product
concepts and launch calendars; and the terms of this Agreement.

The Contractor shall hold Confidential Information in confidence, shall not disclose it to anyone
without the Company's prior written consent, and shall not use it for any purpose other than
performing the Services. The Contractor shall not use Confidential Information to develop, formulate,
market, sell or advise on any product for itself or any third party. These obligations continue for
[CONFIDENTIALITY PERIOD] after the Services end and, for anything the Company treats as a trade
secret, for as long as it remains one.

Confidential Information does not include information the Contractor can show was already known to
it without a duty of confidence, is or becomes public through no act of the Contractor, is
rightfully received from a third party free of any duty of confidence, or was independently
developed without reference to the Company's information. If the Contractor is required by legal
process to disclose Confidential Information it shall, where it is permitted to, notify the Company
promptly and disclose only what is required.

The Contractor shall return or destroy all Confidential Information, samples, product, materials,
equipment and Company property on request or on termination, and shall confirm in writing that it
has done so.

8. OWNERSHIP OF WORK PRODUCT
"Work Product" means everything the Contractor conceives, creates, designs, writes, photographs,
films, records, codes or produces, alone or with others, in the course of or arising from the
Services, including the Deliverables and all drafts, concepts, sketches, rejected routes, outtakes,
data, reports, recipes, formulations, specifications and working files created along the way.

The Contractor hereby irrevocably assigns to the Company all right, title and interest worldwide in
and to the Work Product, including all copyrights, trademark rights, patent rights, trade secret
rights and other intellectual property rights in it, together with the right to sue for and recover
damages for any past, present or future infringement or misappropriation. This is a present
assignment, effective as each item of Work Product is created, and it is not a promise to assign in
the future. Ownership vests in the Company as the Work Product is created and is not conditional on
payment; any unpaid amount is a debt owed to the Contractor and is not a basis for withholding,
revoking or re-taking ownership of, or access to, the Work Product.

Prior materials. The Contractor's pre-existing materials listed on Schedule B (the "Prior
Materials") are not assigned and remain the Contractor's property. Prior Materials are only those
things the Contractor owned or created before the Services began, or created independently of them,
and that are listed on Schedule B. Anything not listed on Schedule B is Work Product. Where any
Prior Material is embedded in, delivered with, or reasonably necessary to use, modify or reproduce a
Deliverable, the Contractor grants the Company a non-exclusive, worldwide, perpetual, irrevocable,
fully paid, transferable and sublicensable licence to use, reproduce, modify, create derivatives
from, distribute and display that Prior Material as part of or in connection with the Deliverable,
including on packaging, in retailer and marketplace listings, in trade materials and in paid
advertising.

Files. Delivery of flattened, exported, watermarked or read-only files alone is not delivery. The
Contractor shall provide the Work Product in native editable form, including layered design files
with live type and linked assets, dielines and print-ready files, raw camera files and outtakes,
editing project files and masters, spreadsheets with formulas intact, and source code with build
instructions, as applicable to the Deliverable.

Waiver and further assurances. To the extent permitted, the Contractor waives, and agrees not to
assert against the Company, its successors, licensees or customers, any right of attribution or
integrity or any similar right, however described, that the Contractor may have in the Work Product;
where such a right cannot be waived, the Contractor agrees not to enforce it. The Contractor shall,
promptly on request and at the Company's expense, sign any document and take any action the Company
reasonably requires to obtain, perfect, record or enforce its rights in the Work Product. This
obligation continues after termination.

9. THIRD-PARTY MATERIAL
Schedule C lists every third-party font, stock image, illustration, music track, plug-in, template,
dataset or other licensed asset embedded in, or required to use, the Deliverables, together with the
licence covering it and who holds that licence. The Contractor represents that each listed licence
permits the Company's intended use, which includes reproduction on primary and secondary packaging,
retailer and marketplace listings, trade and shopper materials, paid advertising and out-of-home,
and permits the Company to transfer or sublicense those uses to its distributors, retailers and
successors. The Contractor shall not incorporate any third-party material that is not listed on
Schedule C without the Company's prior written consent.

10. RELATIONSHIP OF THE PARTIES
The parties intend that the Contractor perform the Services as an independent business and not as an
employee of the Company. Consistent with that intent, and except where a site, safety, brand or
customer requirement applies:

(a) the Contractor determines the manner, method and means by which the Services are performed, and
    the Company's direction is directed at the result rather than the technique;
(b) the Contractor sets its own working hours within the deadlines and event windows in Schedule A;
(c) the Contractor supplies its own tools, equipment, software and workspace, other than Company
    product, samples, brand assets and materials provided for the Services;
(d) the Contractor is free to perform services for others, subject only to Sections 7 and 11;
(e) the Contractor is responsible for its own taxes, filings, registrations and insurance, and for
    any person it engages; and
(f) the Contractor is not eligible to participate in, and does not accrue any right under, any
    benefit, leave, bonus or insurance plan the Company maintains for its employees.

This Section records what the parties intend and how they expect the engagement to operate. It does
not by itself decide how the relationship is characterised for any purpose. If the Services in
practice begin to operate differently from this Section — for example through set hours, exclusivity,
supervision of daily work, or use of the Company's premises and equipment as the norm — either party
may raise it, and the parties shall review the arrangement and take their own advice on it.

11. NO AUTHORITY
The Contractor has no authority to bind the Company, to enter into any agreement on its behalf, to
make any commitment, price, discount or promise to any retailer, distributor, co-manufacturer,
supplier or consumer, or to hold itself out as an employee, officer or agent of the Company. The
Contractor shall not use the Company's name, marks or brand assets except as needed to perform the
Services, and shall not make any public statement about the Company, the products or the engagement
without the Company's prior written consent. Any commitment the Contractor purports to make without
written authority is the Contractor's own.

12. INSURANCE
Where the Services are performed at a retailer, distributor, third-party logistics provider,
co-manufacturer, event venue or other site the Company does not control, or involve handling,
preparing or sampling product in public, the Contractor shall maintain [INSURANCE COVERAGE] at its
own cost for the term and shall provide certificates of insurance before the first day of Services
and on renewal. The Contractor shall name the Company as an additional insured where the site or the
Company reasonably requires it, and shall notify the Company promptly if any required coverage
lapses. The Contractor shall meet any additional insurance, background, food-handling or safety
requirement a site imposes, and shall not perform Services at a site until it does.

13. SUBCONTRACTORS
The Contractor shall not subcontract any part of the Services without the Company's prior written
consent. Before an approved subcontractor begins, the Contractor shall bind it in writing to
confidentiality and assignment obligations at least as protective as Sections 7 and 8, and shall
secure for the Company all rights the subcontractor creates. The Contractor remains responsible for
its subcontractors and for anyone it engages.

14. WARRANTIES
The Contractor represents and warrants that: it has full authority to enter into this Agreement and
is not bound by any obligation to a former employer or any other party that conflicts with it; it
will not bring to the engagement, or use in the Services, any confidential material belonging to a
third party; the Work Product is original to the Contractor except for the Prior Materials on
Schedule B and the third-party material on Schedule C; the Work Product does not, to the best of the
Contractor's knowledge, infringe or misappropriate the rights of any third party; and it holds every
licence, permit and certification required for it to perform the Services.

15. GOVERNING LAW AND VENUE
This Agreement is governed by the laws of the State of [GOVERNING LAW STATE], without regard to its
conflict of laws principles. The parties consent to venue in [VENUE].

16. GENERAL
This Agreement, together with its Schedules, is the entire agreement between the parties regarding
its subject matter and supersedes all prior discussions, proposals and invoices. It may be amended
only in a writing signed by both parties. The Contractor may not assign this Agreement without the
Company's prior written consent; the Company may assign it to a successor in connection with a
merger or a sale of substantially all of its assets. Notices are given in writing to the addresses
above. If any provision is held unenforceable, the remainder continues in effect. Neither party
waives a right by not enforcing it on one occasion. This Agreement may be signed in counterparts,
including by electronic signature, each of which is an original.

IN WITNESS WHEREOF, the parties have executed this Agreement as of the Effective Date.


[COMPANY NAME]                            [CONTRACTOR NAME]


By: _______________________________       By: _______________________________

Name: _____________________________       Name: _____________________________

Title: ____________________________       Title: ____________________________

Date: _____________________________       Date: _____________________________


─────────────────────────────────────────────────────────────────────────────
SCHEDULE A — SCOPE OF WORK
─────────────────────────────────────────────────────────────────────────────
Describe the Services, the deliverables, the dates and the territory or sites. Say what "done"
means for each item. Attach the brief, playbook or shot list rather than describing it here.

Services and scope:

[SCOPE OF WORK]

_____________________________________________________________________________

Deliverables, formats and due dates:

[DELIVERABLES]

_____________________________________________________________________________

Fee basis for this scope: ____________________________________________________

Territory, sites or events: __________________________________________________

Contractor initials: __________          Company initials: __________


─────────────────────────────────────────────────────────────────────────────
SCHEDULE B — CONTRACTOR PRIOR MATERIALS
─────────────────────────────────────────────────────────────────────────────
Everything the Contractor owned or created before this engagement, or independently of it, that is
carved out of the assignment in Section 8. Anything not listed here is Work Product and belongs to
the Company. Write "None" if there is none.

[PRIOR MATERIALS]

_____________________________________________________________________________

_____________________________________________________________________________

Contractor initials: __________          Company initials: __________


─────────────────────────────────────────────────────────────────────────────
SCHEDULE C — THIRD-PARTY MATERIAL AND LICENCES
─────────────────────────────────────────────────────────────────────────────
Every third-party font, image, illustration, music track, plug-in, template or dataset inside or
needed for the Deliverables, the licence covering it, who holds it, and whether it reaches
packaging, resale and paid media. Write "None" if there is none.

[THIRD PARTY MATERIALS]

_____________________________________________________________________________

_____________________________________________________________________________

Contractor initials: __________          Company initials: __________
`,
  },

  {
    id: "consultant-agreement",
    docId: "consultant-agreement",
    title: "CONSULTANT AGREEMENT",
    version: "1.0",
    lastReviewed: "2026-08-25",

    founderGuide: {
      useThisWhen:
        "You are buying judgement rather than output: a food scientist, a supply chain operator, a retail category veteran, a fractional finance or marketing lead. The value is their advice and their access, usually on a retainer or a day rate, and the deliverable is often a conversation rather than a file.",
      whyItMatters:
        "CPG consultants are valuable precisely because they work across many brands, and that is also the exposure. The person helping you land a chain account this quarter may be helping two competitors next quarter, using the shelf plan, the landed cost and the buyer feedback you paid to generate. The frameworks they bring are genuinely theirs and it is reasonable that they reuse them — but the version built with your pricing, your formulation and your account data should not travel. A consultant agreement that only bars disclosure and never bars use gives you almost nothing, because nobody forwards a document; they just know it. Get the disclosure obligation, get non-use, and name the individual whose judgement you are actually buying.",
      negotiateCarefully: [
        {
          point: "Non-use matters more than non-disclosure",
          detail:
            "A consultant who never repeats your co-packer's tolling rate but prices a competitor's programme against it has not disclosed anything. Add an express non-use obligation naming formulations, landed cost, margin structure, promotional calendars and buyer feedback, and accept that the general frameworks in their head are not what you are restricting.",
        },
        {
          point: "Disclosure of competing engagements, on signing and on change",
          detail:
            "A consultant working three brands in one category is normal; undisclosed overlap is the problem. Require a written list of current clients in your category at the start and notice before they take on a new one, so you can decide what to stop showing them rather than finding out at a shelf reset.",
        },
        {
          point: "Name the person, not just the firm",
          detail:
            "You are paying for a specific operator's judgement and relationships. Without a named-consultant clause the work goes to an analyst you have never met while you pay the senior rate, and the introductions you were actually buying never happen.",
        },
        {
          point: "Own the work built on your data; let them keep their toolkit",
          detail:
            "Assign the memos, models, specifications and formulations produced for you, and licence back the consultant's genuinely pre-existing frameworks rather than the other way round. A consultant who insists on owning everything and giving you a licence is selling you the same asset twice.",
        },
        {
          point: "An exit that does not cost three months of retainer",
          detail:
            "Retainers stop producing value long before anyone says so. Keep the notice period short after any agreed initial period, pay for work actually done rather than the balance of the term, and make sure any success fee has a written definition of what counts and what happens to deals in flight when you part.",
        },
      ],
      redFlags: [
        "No conflicts or disclosure obligation in a category where the consultant obviously has other clients.",
        "Confidentiality that bars disclosure but says nothing about use.",
        "The consultant owns everything produced and grants the brand a licence to it.",
        "A long minimum retainer with no termination right and fees payable for the balance of the term.",
        "A success fee with no definition of a qualifying deal or of what happens after termination.",
        "Open-ended authority to negotiate with your co-packer, supplier, distributor or retail accounts.",
      ],
      timeToComplete: "20 minutes to fill · same-day to sign",
    },

    placeholders: [
      { token: "[EFFECTIVE DATE]", label: "Effective date", example: "September 1, 2026" },
      { token: "[COMPANY NAME]", label: "Your legal entity name", brandKey: "legalEntityName", example: "Acme Beverage Co., LLC" },
      { token: "[COMPANY STATE]", label: "Your state of formation", brandKey: "stateOfFormation", example: "Delaware" },
      { token: "[COMPANY ENTITY TYPE]", label: "Your entity type", example: "limited liability company" },
      { token: "[COMPANY ADDRESS]", label: "Your address", brandKey: "principalAddress", example: "123 Main St, Austin, TX 78701" },
      { token: "[BRAND NAME]", label: "Your brand name", brandKey: "tradeName", example: "Acme" },
      { token: "[CONSULTANT NAME]", label: "Consultant's or firm's legal name", example: "Harlow Category Partners LLC" },
      { token: "[CONSULTANT ADDRESS]", label: "Consultant's address", example: "410 Bay St, Suite 9, Chicago, IL 60611" },
      { token: "[NAMED INDIVIDUAL]", label: "The individual who must do the work", example: "Priya Harlow" },
      { token: "[ADVISORY SCOPE]", label: "What the consultant advises on (Schedule A)", example: "national grocery channel strategy, buyer targeting and promotional planning" },
      { token: "[TIME COMMITMENT]", label: "Expected time commitment", example: "up to 20 hours per month, including two standing calls" },
      { token: "[AVAILABILITY]", label: "Availability and response expectations", example: "a standing weekly call plus response to email within two business days" },
      { token: "[FEE BASIS]", label: "Retainer, day rate or hourly fee", example: "$6,000 per month retainer covering up to 20 hours" },
      { token: "[OVERAGE RATE]", label: "Rate for work beyond the committed time", example: "$300 per hour, pre-approved in writing" },
      { token: "[INVOICE FREQUENCY]", label: "How often the consultant invoices", example: "monthly, in advance for the retainer" },
      { token: "[PAYMENT PERIOD]", label: "Time to pay a correct invoice", example: "15 days from receipt" },
      { token: "[EXPENSE APPROVAL THRESHOLD]", label: "Expense amount needing prior approval", example: "$500 per item" },
      { token: "[INITIAL PERIOD]", label: "Minimum initial engagement period", example: "3 months" },
      { token: "[TERMINATION NOTICE PERIOD]", label: "Notice to end the engagement after the initial period", example: "30 days" },
      { token: "[CONFIDENTIALITY PERIOD]", label: "How long confidentiality and non-use run afterwards", example: "3 years" },
      { token: "[COMPETING CATEGORY]", label: "The category defining a competing engagement", example: "ready-to-drink canned functional beverages sold in US grocery" },
      { token: "[DISCLOSED ENGAGEMENTS]", label: "Consultant's disclosed clients in the category (Schedule B)", example: "one hydration brand, DTC only, no grocery overlap" },
      { token: "[CONSULTANT BACKGROUND MATERIALS]", label: "Consultant's pre-existing frameworks (Schedule C)", example: "Harlow channel-sizing model; buyer meeting template" },
      { token: "[GOVERNING LAW STATE]", label: "Governing law state", example: "Delaware" },
      { token: "[VENUE]", label: "Venue for disputes", example: "Wilmington, Delaware" },
    ],

    body: `This Consultant Agreement (this "Agreement") is entered into as of [EFFECTIVE DATE] (the
"Effective Date") by and between:

  [COMPANY NAME], a [COMPANY STATE] [COMPANY ENTITY TYPE], with a principal place of
  business at [COMPANY ADDRESS], which markets products under the brand [BRAND NAME]
  (the "Company"); and

  [CONSULTANT NAME], with an address at [CONSULTANT ADDRESS] (the "Consultant").

1. ENGAGEMENT AND SCOPE
The Company engages the Consultant to advise it on [ADVISORY SCOPE] (the "Services"), as further
described in Schedule A. The Services are advisory. The Consultant's obligation is to apply its
professional judgement, experience and knowledge of the Company's category, to be available as set
out in Section 2, and to give the Company candid advice. The Consultant does not guarantee any
commercial outcome, and the Company remains responsible for its own decisions.

Where Schedule A also identifies specific work product — a memo, a model, a specification, a plan or
a formulation — the Consultant shall produce it to the timing stated there. Schedule A may be
replaced or supplemented by a further schedule signed by both parties.

2. TIME COMMITMENT AND AVAILABILITY
The Consultant shall make available [TIME COMMITMENT] to the Services. The parties expect
[AVAILABILITY]. Time beyond the committed amount is worked only where the Company approves it in
writing in advance and is charged at [OVERAGE RATE]. Unused committed time does not carry forward
unless Schedule A says it does.

3. NAMED CONSULTANT
The Company is engaging the judgement and experience of [NAMED INDIVIDUAL], and [NAMED INDIVIDUAL]
shall personally perform the Services. The Consultant shall not delegate or subcontract any part of
the Services without the Company's prior written consent. Where consent is given, the Consultant
shall bind that person in writing to obligations at least as protective as Sections 6, 7 and 8, and
remains responsible for their work.

4. FEES
The Company shall pay the Consultant [FEE BASIS]. The Consultant shall invoice [INVOICE FREQUENCY]
and shall provide, with each invoice, a short summary of the Services performed in the period and,
where the fee is calculated on time, a record of hours. The Company shall pay each correct and
undisputed invoice within [PAYMENT PERIOD].

Any success fee, commission or equity element is payable only where Schedule A sets it out in
writing, and Schedule A must state what qualifies, when the fee is earned, how it is calculated, and
what is payable on transactions that close after this Agreement ends. Absent that, no success fee is
payable. The Consultant is responsible for its own taxes, filings and registrations arising from the
fees.

5. EXPENSES
The Company reimburses reasonable expenses incurred directly for the Services and supported by
receipts. Any single expense above [EXPENSE APPROVAL THRESHOLD], and any travel, trade show cost,
research purchase, subcontractor cost or third-party licence, requires the Company's prior written
approval. Expenses are reimbursed at cost, without markup.

6. CONFIDENTIALITY AND NON-USE
"Confidential Information" means the Company's non-public information in any form, whether or not
marked confidential, including: formulations, recipes, specifications, process parameters and
shelf-life data; ingredient identities, sources and landed cost; co-manufacturer tolling rates,
supplier terms and freight rates; margin structure, pricing architecture, promotional calendars,
trade spend and deduction history; retailer and distributor terms, planogram position and buyer
feedback; customer and account lists and pipeline; forecasts, sales data and financial information;
unreleased products, packaging and launch plans; and the terms of this Agreement.

The Consultant shall hold Confidential Information in confidence and shall not disclose it to anyone
without the Company's prior written consent. Separately, and as an independent obligation, the
Consultant shall not use Confidential Information for any purpose other than performing the
Services. In particular the Consultant shall not use Confidential Information in, or for the benefit
of, any engagement with any other party, and shall not use it to develop, formulate, price, position
or advise on any product competing with the Company's products.

These obligations continue for [CONFIDENTIALITY PERIOD] after this Agreement ends and, for anything
the Company treats as a trade secret, for as long as it remains one. They do not apply to
information the Consultant can show was already known to it without a duty of confidence, is or
becomes public through no act of the Consultant, is rightfully received from a third party free of
any duty of confidence, or was independently developed without reference to the Company's
information. If the Consultant is required by legal process to disclose Confidential Information it
shall, where it is permitted to, notify the Company promptly and disclose only what is required.

On termination or on request the Consultant shall return or destroy all Confidential Information,
including models, decks, exports and copies held in personal accounts, and shall confirm in writing
that it has done so. The Consultant may retain one archival copy for its own compliance records,
which remains subject to this Section.

7. CONFLICTS AND DISCLOSURE OF COMPETING ENGAGEMENTS
The Consultant may work for other clients, and the Company does not require exclusivity except as
set out in this Section.

Disclosure on signing. Schedule B lists every engagement the Consultant currently has, and every
engagement it is actively discussing, with any business in [COMPETING CATEGORY], and any ownership,
board, advisory, commission or referral interest the Consultant holds in any co-manufacturer,
ingredient supplier, broker, distributor or retailer that the Company deals with or is likely to
deal with.

Disclosure on change. Before the Consultant accepts any new engagement, interest or role of a kind
described above, it shall notify the Company in writing. The Company may then, within a period the
parties agree in writing, either consent, consent subject to agreed information barriers, or end
this Agreement on written notice without further fee liability beyond work already performed.

Conduct during an overlap. Where the Company consents to an overlapping engagement, the Consultant
shall not work on both engagements in a way that uses the Company's Confidential Information, shall
not participate on both sides of any negotiation involving the Company, and shall tell the Company
promptly if a conflict arises that it cannot manage. The Consultant shall disclose to the Company
any fee, commission, rebate or other benefit it receives or expects to receive from any third party
in connection with a recommendation it makes to the Company.

8. OWNERSHIP OF WORK PRODUCT
"Work Product" means everything the Consultant creates, writes, models, designs, formulates,
specifies or produces for the Company in the course of or arising from the Services, including
memos, decks, models, plans, specifications, formulations, process documents, research, data
compilations and the underlying working files.

The Consultant hereby irrevocably assigns to the Company all right, title and interest worldwide in
and to the Work Product, including all copyrights, trade secret rights and other intellectual
property rights in it. This is a present assignment, effective as each item of Work Product is
created, and it is not a promise to assign in the future. Ownership is not conditional on payment;
any unpaid amount is a debt owed to the Consultant and is not a basis for withholding, revoking or
re-taking ownership of, or access to, the Work Product.

Background materials. The Consultant's pre-existing frameworks, methodologies, models and templates
listed on Schedule C (the "Background Materials") are not assigned and remain the Consultant's
property. Anything not listed on Schedule C is Work Product. Where a Background Material is embedded
in, or reasonably necessary to use or update, any Work Product, the Consultant grants the Company a
non-exclusive, worldwide, perpetual, irrevocable, fully paid, transferable and sublicensable licence
to use, reproduce, modify and create derivatives from it for the Company's own business purposes.
The Consultant may continue to use its Background Materials for other clients, but shall not reuse
any Work Product, or any version of a Background Material populated with the Company's Confidential
Information, for any other client.

Delivery and further assurances. The Consultant shall deliver Work Product in native editable form,
with formulas, sources and assumptions intact, and shall not deliver locked or flattened files
alone. The Consultant shall, promptly on request and at the Company's expense, sign any document and
take any action the Company reasonably requires to perfect or record its rights in the Work Product.
To the extent permitted, the Consultant waives, and agrees not to assert against the Company, any
right of attribution or integrity or any similar right, however described, in the Work Product.

9. NO AUTHORITY
The Consultant has no authority to bind the Company, to enter into any agreement on its behalf, or
to commit the Company to any price, discount, volume, term, promotion or arrangement with any
co-manufacturer, supplier, broker, distributor, retailer or customer. Where the Company asks the
Consultant to speak with a third party on its behalf, the Consultant shall act within the written
brief it is given and shall confirm material discussions back to the Company in writing. The
Consultant shall not make any public statement about the Company, the products or the engagement
without the Company's prior written consent.

10. INDEPENDENT CONTRACTOR
The parties intend that the Consultant provide the Services as an independent business and not as an
employee of the Company. The Consultant determines how the Services are performed, supplies its own
tools, equipment and workspace, is free to serve other clients subject to Sections 6 and 7, is
responsible for its own taxes, filings, registrations and insurance, and is not eligible to
participate in any benefit plan the Company maintains for its employees. This Section records what
the parties intend and does not by itself decide how the relationship is characterised for any
purpose; if the engagement in practice operates differently from what is written here, either party
may raise it and the parties shall review the arrangement and take their own advice on it.

11. TERM AND TERMINATION
This Agreement begins on the Effective Date and continues for [INITIAL PERIOD], after which it
continues until ended under this Section.

After the initial period, either party may end this Agreement for any reason on
[TERMINATION NOTICE PERIOD] written notice. Either party may end it immediately on written notice if
the other materially breaches and does not cure within [TERMINATION NOTICE PERIOD] of written notice
describing the breach. The Company may end it immediately if the Consultant breaches Section 6 or
Section 7, or if [NAMED INDIVIDUAL] ceases to be available to perform the Services.

On termination the Company shall pay for Services properly performed up to the termination date and
for approved expenses already incurred, and the Consultant shall refund the unearned portion of any
retainer paid in advance. Neither party owes the other the balance of an unperformed term. The
Consultant shall promptly deliver all Work Product in its then-current form and shall comply with
Sections 6 and 8. Sections 6, 7, 8, 9, 12 and 13 survive termination.

12. WARRANTIES
The Consultant represents and warrants that: it has full authority to enter into this Agreement and
is not bound by any obligation to any other client or former employer that conflicts with it; it
will not disclose to the Company, or use in the Services, any confidential information belonging to
a third party; Schedule B is complete and accurate as at the Effective Date; the Work Product is
original to the Consultant except for the Background Materials on Schedule C; and it holds every
licence, permit and certification required for it to perform the Services.

13. GOVERNING LAW AND VENUE
This Agreement is governed by the laws of the State of [GOVERNING LAW STATE], without regard to its
conflict of laws principles. The parties consent to venue in [VENUE].

14. GENERAL
This Agreement, together with its Schedules, is the entire agreement between the parties regarding
its subject matter and supersedes all prior discussions and proposals. It may be amended only in a
writing signed by both parties. The Consultant may not assign this Agreement without the Company's
prior written consent; the Company may assign it to a successor in connection with a merger or a
sale of substantially all of its assets. Notices are given in writing to the addresses above. If any
provision is held unenforceable, the remainder continues in effect. This Agreement may be signed in
counterparts, including by electronic signature, each of which is an original.

IN WITNESS WHEREOF, the parties have executed this Agreement as of the Effective Date.


[COMPANY NAME]                            [CONSULTANT NAME]


By: _______________________________       By: _______________________________

Name: _____________________________       Name: _____________________________

Title: ____________________________       Title: ____________________________

Date: _____________________________       Date: _____________________________


─────────────────────────────────────────────────────────────────────────────
SCHEDULE A — SCOPE, CADENCE AND ANY SUCCESS FEE
─────────────────────────────────────────────────────────────────────────────
Describe what the Consultant advises on, the standing cadence, and any specific work product with
its timing. If a success fee, commission or equity element applies, set out here what qualifies,
when it is earned, how it is calculated and what happens to transactions closing after termination.
If nothing is written here, no success fee is payable.

Advisory scope:

[ADVISORY SCOPE]

_____________________________________________________________________________

Cadence and specific work product:

_____________________________________________________________________________

Success fee, commission or equity (or "None"): _______________________________

Consultant initials: __________          Company initials: __________


─────────────────────────────────────────────────────────────────────────────
SCHEDULE B — DISCLOSED ENGAGEMENTS AND INTERESTS
─────────────────────────────────────────────────────────────────────────────
Every current or actively discussed engagement with a business in [COMPETING CATEGORY], and every
ownership, board, advisory, commission or referral interest in any co-manufacturer, ingredient
supplier, broker, distributor or retailer the Company deals with or is likely to deal with. Write
"None" if there is none.

[DISCLOSED ENGAGEMENTS]

_____________________________________________________________________________

_____________________________________________________________________________

Consultant initials: __________          Company initials: __________


─────────────────────────────────────────────────────────────────────────────
SCHEDULE C — CONSULTANT BACKGROUND MATERIALS
─────────────────────────────────────────────────────────────────────────────
The Consultant's pre-existing frameworks, models, templates and methodologies carved out of the
assignment in Section 8. Anything not listed here is Work Product and belongs to the Company. Write
"None" if there is none.

[CONSULTANT BACKGROUND MATERIALS]

_____________________________________________________________________________

_____________________________________________________________________________

Consultant initials: __________          Company initials: __________
`,
  },

  {
    id: "contractor-work-for-hire-agreement",
    docId: "contractor-work-for-hire-agreement",
    title: "CONTRACTOR WORK-FOR-HIRE AGREEMENT",
    version: "1.0",
    lastReviewed: "2026-08-25",

    founderGuide: {
      useThisWhen:
        "You are commissioning a freelancer or a studio to make something the brand has to own outright: the logo, the label, the can artwork, the product photography, the video, the website, the illustration, the dielines. Sign it before they start, not with the invoice.",
      whyItMatters:
        "This is the most common expensive gap in a young CPG brand. A brief, an invoice and a thank-you do not move ownership — the designer who made your label keeps it until they assign it in writing. The consequences all arrive at once and late: you cannot register the artwork, you cannot license the brand, you cannot make a compliance change to the panel without the layered file, and a buyer's diligence list asks for the assignment you never got. This template puts the present assignment first and treats work-for-hire as an additional characterisation the parties agree to, so ownership does not depend on the work fitting that description. Buying rights back later, from a freelancer who can see exactly how well the brand is doing, is not a negotiation worth having.",
      negotiateCarefully: [
        {
          point: "Assignment first, work-for-hire second",
          detail:
            "Order matters. If the agreement leans on the work-for-hire label and adds assignment as an afterthought, anything the label does not reach can sit outside the transfer. Put a present assignment that operates on creation at the front, and treat work-for-hire as an extra characterisation the parties agree to rather than the mechanism you are relying on.",
        },
        {
          point: "Deliverables are files, not outcomes",
          detail:
            "'Final artwork' is satisfied by a flattened PDF. Name the layered native files with live type, the linked assets, the packaged fonts, the dieline, the print-ready and prepress files, the raw camera files and outtakes, the editing project files and the 3D scenes. Owning an image you cannot edit is the most common form of half-ownership in this category.",
        },
        {
          point: "Ownership should not hang on the last invoice",
          detail:
            "Conditioning the assignment on payment in full turns a billing dispute into an ownership dispute at the moment you have a press date booked. Move ownership to creation and hold a meaningful final payment against delivery of the working files instead — that is the leverage that actually gets you the files.",
        },
        {
          point: "The moral-rights waiver is something they grant, not something you assume",
          detail:
            "Draft it as an explicit waiver and a covenant not to assert, with a fallback consent where a right cannot be waived. Do the same for subcontractors: the illustrator or retoucher the studio hired is the real creator, and their rights only reach you if the studio was required in writing to secure them.",
        },
        {
          point: "Fonts, stock and everything else borrowed",
          detail:
            "Desktop font licences and standard stock licences frequently do not cover mass-reproduced packaging or products for resale, and the shortfall surfaces at pre-press with a press date booked. Get the list before work starts, and get a warranty that each licence reaches packaging, resale, retailer listings and paid media, and can be transferred to your distributors and a future acquirer.",
        },
      ],
      redFlags: [
        "Work-for-hire wording with no present assignment, or an assignment written as a promise to assign later.",
        "Ownership or file delivery conditional on payment in full, with no interim licence to keep a print run moving.",
        "Deliverables defined as final exports, with source, layered and print-ready files excluded or charged extra.",
        "No disclosure of fonts, stock, illustration or music, or licences that stop short of packaging and resale.",
        "The contractor reserving the right to reuse elements of your design for other clients.",
        "No obligation to secure written rights from the subcontractors who will actually do the work.",
      ],
      timeToComplete: "25 minutes to fill · same-day to sign",
    },

    placeholders: [
      { token: "[EFFECTIVE DATE]", label: "Effective date", example: "September 1, 2026" },
      { token: "[COMPANY NAME]", label: "Your legal entity name", brandKey: "legalEntityName", example: "Acme Beverage Co., LLC" },
      { token: "[COMPANY STATE]", label: "Your state of formation", brandKey: "stateOfFormation", example: "Delaware" },
      { token: "[COMPANY ENTITY TYPE]", label: "Your entity type", example: "limited liability company" },
      { token: "[COMPANY ADDRESS]", label: "Your address", brandKey: "principalAddress", example: "123 Main St, Austin, TX 78701" },
      { token: "[BRAND NAME]", label: "Your brand name", brandKey: "tradeName", example: "Acme" },
      { token: "[CONTRACTOR NAME]", label: "Contractor's or studio's legal name", example: "Rill Design Studio LLC" },
      { token: "[CONTRACTOR ADDRESS]", label: "Contractor's address", example: "18 Bell Ave, Portland, OR 97209" },
      { token: "[PROJECT DESCRIPTION]", label: "The commissioned project", example: "brand identity and primary label artwork for a four-SKU canned tea line" },
      { token: "[DELIVERABLES]", label: "Deliverables (Schedule A), named as files", example: "logo suite, four label artworks, dieline, layered .ai source, packaged fonts, print-ready PDFs" },
      { token: "[SOURCE FILE FORMATS]", label: "Required source and working file formats", example: "layered .ai and .psd, packaged .indd, camera RAW, .prproj project files" },
      { token: "[DELIVERY DATE]", label: "Delivery date for the Deliverables", example: "October 15, 2026" },
      { token: "[REVISION ROUNDS]", label: "Revision rounds included in the fee", example: "two rounds per deliverable" },
      { token: "[ADDITIONAL REVISION RATE]", label: "Rate for revisions beyond those rounds", example: "$120 per hour, pre-approved in writing" },
      { token: "[FEE]", label: "Fee for the work", example: "$18,000, invoiced in three stages" },
      { token: "[FINAL PAYMENT SHARE]", label: "Portion of the fee held until working files arrive", example: "the final third of the fee" },
      { token: "[PAYMENT PERIOD]", label: "Time to pay a correct invoice", example: "15 days from receipt" },
      { token: "[TERMINATION NOTICE PERIOD]", label: "Notice to end the project", example: "10 days" },
      { token: "[PUBLIC LAUNCH DATE]", label: "Date portfolio use may begin", example: "the public launch date of the product" },
      { token: "[PRIOR MATERIALS]", label: "Contractor's pre-existing materials (Schedule B)", example: "Rill grid system and internal production scripts" },
      { token: "[THIRD PARTY MATERIALS]", label: "Third-party fonts, stock and assets (Schedule C)", example: "Font: Sabon — desktop plus packaging licence, held by Contractor" },
      { token: "[GOVERNING LAW STATE]", label: "Governing law state", example: "Delaware" },
      { token: "[VENUE]", label: "Venue for disputes", example: "Wilmington, Delaware" },
    ],

    body: `This Contractor Work-for-Hire Agreement (this "Agreement") is entered into as of [EFFECTIVE DATE]
(the "Effective Date") by and between:

  [COMPANY NAME], a [COMPANY STATE] [COMPANY ENTITY TYPE], with a principal place of
  business at [COMPANY ADDRESS], which markets products under the brand [BRAND NAME]
  (the "Company"); and

  [CONTRACTOR NAME], with an address at [CONTRACTOR ADDRESS] (the "Contractor").

1. THE COMMISSION
The Company commissions the Contractor to create the following: [PROJECT DESCRIPTION] (the
"Project"). The Contractor shall create and deliver the items listed on Schedule A (the
"Deliverables") by [DELIVERY DATE]. The Deliverables are commissioned by the Company for the
Company's own use and are created to the Company's order, brief and specification.

"Work Product" means everything the Contractor conceives, creates, designs, writes, photographs,
films, records, codes or produces, alone or with others, in the course of or arising from the
Project, including the Deliverables and all drafts, concepts, sketches, storyboards, rejected
routes, outtakes, unselected frames, layouts, dielines, colour builds, retouching layers and working
files created along the way.

2. PRESENT ASSIGNMENT OF THE WORK PRODUCT
The Contractor hereby irrevocably assigns to the Company all right, title and interest worldwide in
and to the Work Product, including all copyrights, trademark rights, design rights, patent rights,
trade secret rights and other intellectual property rights in it, together with the right to sue for
and recover damages for any past, present or future infringement or misappropriation.

This is a present assignment. It operates as each item of Work Product is created and is not a
promise to assign at some later time. Ownership vests in the Company on creation and is not
conditional on payment, on delivery, on acceptance or on any Deliverable qualifying as any
particular kind of work. Any unpaid amount is a debt owed to the Contractor and is not a basis for
withholding, revoking or re-taking ownership of, or access to, the Work Product.

To the extent any right in the Work Product cannot be assigned, the Contractor grants the Company an
exclusive, worldwide, perpetual, irrevocable, fully paid, transferable and sublicensable licence to
use, reproduce, modify, adapt, translate, create derivative works from, distribute, publicly display,
publicly perform and otherwise exploit that right for any purpose and in any medium now known or
later developed, including on primary and secondary packaging, in retailer and marketplace listings,
in trade and shopper materials, in paid advertising and out-of-home, and by the Company's
distributors, retailers, licensees and successors.

3. WORK MADE FOR HIRE — ADDITIONAL CHARACTERISATION
In addition to Section 2, and without limiting or qualifying it, the parties agree that the Work
Product is specially ordered and commissioned by the Company, is created for the Company at its
instance and expense and to its specification, and is to be treated as work made for hire for the
Company wherever that characterisation is available.

The parties do not rely on that characterisation. Ownership of the Work Product passes to the
Company under Section 2 whether or not any Work Product is, or could be, treated as work made for
hire, and nothing in this Section conditions, limits or delays Section 2. If this Section is for any
reason ineffective as to any Work Product, Section 2 applies to it in full.

4. WAIVER OF ATTRIBUTION AND INTEGRITY RIGHTS
To the fullest extent permitted, the Contractor irrevocably waives, and agrees never to assert
against the Company or its successors, licensees, distributors, retailers or customers, any right of
attribution, integrity, disclosure, withdrawal or association, and any similar right however
described, that the Contractor may have or acquire in the Work Product.

Where such a right cannot be waived, the Contractor agrees not to enforce it and consents to the
Company and its licensees doing anything that would otherwise engage it, including modifying,
cropping, recolouring, retouching, re-cutting, combining with other material, adapting to a
different pack format or substrate, and using the Work Product without credit. The Contractor shall
secure the same waiver and consent, in writing, from every person who contributes to the Work
Product, and this Section is granted for the benefit of the Company and anyone claiming through it.

5. DELIVERABLES — SOURCE, WORKING AND EDITABLE FILES
Delivery of flattened, exported, watermarked, read-only or proof files alone is not delivery. The
Deliverables include the editable material, and the Contractor shall deliver the Work Product in
native editable form in the following formats: [SOURCE FILE FORMATS], including as applicable:

(a) layered design and artwork files with live editable type, named layers, all artboards and all
    linked assets supplied, packaged and unflattened;
(b) the dieline or structural file matching the converter's or printer's specification, in editable
    vector form, together with the printer's or converter's specification sheet it was built to;
(c) print-ready and prepress files, including colour separations, spot and process colour builds,
    trapping, bleed and any imposition or step-and-repeat file supplied to the printer;
(d) every font actually used, packaged with the file, or a written list identifying each font, its
    foundry and its licence, where the licence does not permit the font file to be transferred;
(e) raw and original camera files, including outtakes and unselected frames, plus retouching layers,
    colour profiles and any LUTs or presets applied;
(f) video and audio project files, sequences, bins, graphics templates, captions, stems and masters,
    together with the media they reference;
(g) 3D scene files, models, textures, materials, lighting rigs and render settings;
(h) code repositories with commit history where practicable, build and deployment instructions,
    content management access and any keys or credentials created for the Project; and
(i) a written index of what has been delivered, so the Company can confirm the package is complete.

The Contractor shall deliver by a method the Company can retain, and shall not deliver only by a
link that expires. On the Company's request at any time after delivery, and for as long as the
Contractor holds them, the Contractor shall re-supply any of these files at no further charge.

6. REVISIONS AND APPROVAL
The fee includes [REVISION ROUNDS]. A round is a single consolidated set of the Company's comments
on a Deliverable. Revisions beyond the included rounds, and any change to an approved brief, are
charged at [ADDITIONAL REVISION RATE] and only where the Company approves the additional work in
writing before it is done. Correcting an error, a departure from the brief, or a defect in a
Deliverable is not a revision round and is at the Contractor's cost.

7. FEE AND PAYMENT
The Company shall pay the Contractor [FEE], invoiced as set out in Schedule A. The Company shall pay
each correct and undisputed invoice within [PAYMENT PERIOD]. [FINAL PAYMENT SHARE] is payable on the
Company's confirmation that the complete file package described in Section 5 has been received and
opens in full on the Company's own systems. Withholding that portion is the Company's remedy for
non-delivery of files; it does not affect ownership, which has already passed under Section 2.

8. THIRD-PARTY MATERIAL
Schedule C lists every third-party font, stock image, illustration, music track, sound effect,
plug-in, template, model or other licensed asset embedded in, or required to use, the Deliverables,
together with the licence covering it and who holds that licence. The Contractor represents that
each listed licence permits the Company's intended use, which includes mass reproduction on primary
and secondary packaging, products for resale, retailer and marketplace listings, trade and shopper
materials, paid advertising and out-of-home, and permits the Company to transfer or sublicense those
uses to its distributors, retailers, licensees and successors. Where a licence must be purchased in
the Company's name, the Contractor shall say so before it is used. The Contractor shall not
incorporate any third-party material not listed on Schedule C without the Company's prior written
consent, and shall not embed any asset whose licence prohibits the uses named in this Section.

9. PRIOR MATERIALS
The Contractor's pre-existing materials listed on Schedule B (the "Prior Materials") are not
assigned and remain the Contractor's property. Prior Materials are only those things the Contractor
owned or created before the Project began, or created wholly independently of it, and that are
listed on Schedule B. Anything not listed on Schedule B is Work Product. Where any Prior Material is
embedded in, delivered with, or reasonably necessary to use, modify or reproduce a Deliverable, the
Contractor grants the Company a non-exclusive, worldwide, perpetual, irrevocable, fully paid,
transferable and sublicensable licence to use, reproduce, modify, create derivatives from,
distribute and display it for the uses named in Section 8. The Contractor shall not use any Work
Product, or any distinctive element created for the Project, for any other client.

10. SUBCONTRACTORS
The Contractor shall not engage any subcontractor, studio, illustrator, retoucher, photographer,
editor, developer, model or other third party for the Project without the Company's prior written
consent. Before an approved subcontractor begins, the Contractor shall bind them in writing to
confidentiality obligations and to assignment and waiver obligations at least as protective as
Sections 2, 4 and 11, and shall secure for the Company all rights that subcontractor creates. On
request the Contractor shall identify every person who worked on the Project and provide evidence of
those written obligations. The Contractor remains responsible for its subcontractors.

11. RELEASES
Where the Work Product includes any identifiable person, private property, artwork, vehicle or
location, the Contractor shall obtain signed model, property and location releases in a form the
Company approves, covering the uses named in Section 8 for the same period and territory as the
Company's rights, and shall deliver copies to the Company with the Deliverables.

12. CONFIDENTIALITY AND EMBARGO
The Contractor shall keep confidential all non-public information of the Company it receives,
including formulations, ingredient sourcing, costs, supplier and co-manufacturer identities,
forecasts, launch calendars and unreleased packaging, shall use it only for the Project, and shall
not publish, post, exhibit or show any Work Product, concept or unreleased packaging before
[PUBLIC LAUNCH DATE] without the Company's prior written consent. This obligation continues after
the Project ends and, for trade secret material, for as long as the information remains a trade
secret.

13. PORTFOLIO AND CREDIT
The Contractor may display finished Deliverables in its portfolio and self-promotional materials
only on and after [PUBLIC LAUNCH DATE], and only in a form that discloses no formulation,
ingredient, cost, supplier, sales or forecast information. The Company is not required to credit the
Contractor on any use, and no credit is required on packaging, retailer listings, trade materials or
paid advertising. The Contractor shall remove any portfolio display on the Company's written
request.

14. WARRANTIES
The Contractor represents and warrants that: it has full authority to enter into this Agreement and
is not bound by any obligation that conflicts with it; the Work Product is original to the
Contractor except for the Prior Materials on Schedule B and the third-party material on Schedule C;
the Work Product does not, to the best of the Contractor's knowledge, infringe or misappropriate the
rights of any third party; it has not granted and will not grant any right in the Work Product to
any other party; and it has secured the rights, waivers and releases required by Sections 4, 10
and 11.

15. TERM AND TERMINATION
This Agreement begins on the Effective Date and continues until the Deliverables are delivered and
paid for, unless ended earlier. Either party may end it on [TERMINATION NOTICE PERIOD] written
notice, and either party may end it immediately if the other materially breaches and does not cure
within [TERMINATION NOTICE PERIOD] of written notice describing the breach.

On termination the Company shall pay for work properly performed to the termination date, and the
Contractor shall promptly deliver all Work Product in its then-current form, in the formats required
by Section 5. Sections 2, 3, 4, 5, 8, 9, 11, 12, 13, 14, 16 and 17 survive termination. Ownership of
Work Product created before termination is unaffected by it.

16. INDEPENDENT CONTRACTOR
The Contractor is engaged as an independent business and not as an employee. The Contractor
determines how the work is performed, supplies its own tools, equipment and workspace, is free to
work for others subject to Sections 9 and 12, and is responsible for its own taxes, filings and
insurance. Nothing in this Agreement creates an employment, partnership, joint venture or agency
relationship, and this Agreement does not obligate the Company to commission further work.

17. FURTHER ASSURANCES
The Contractor shall, promptly on the Company's request and at the Company's expense, sign any
document and take any action the Company reasonably requires to obtain, perfect, record, register or
enforce its rights in the Work Product, including assignments in recordable form and declarations
supporting any application. This obligation continues after delivery and after termination. If the
Company is unable after reasonable effort to obtain the Contractor's signature, the Contractor
appoints the Company as its agent and attorney-in-fact, coupled with an interest, solely to sign
such documents on the Contractor's behalf.

18. GOVERNING LAW AND VENUE
This Agreement is governed by the laws of the State of [GOVERNING LAW STATE], without regard to its
conflict of laws principles. The parties consent to venue in [VENUE].

19. GENERAL
This Agreement, together with its Schedules, is the entire agreement between the parties regarding
its subject matter and supersedes all prior discussions. Where it conflicts with a statement of
work, proposal, quote or invoice, this Agreement controls on ownership, files, third-party material,
releases and confidentiality. It may be amended only in a writing signed by both parties. The
Contractor may not assign this Agreement without the Company's prior written consent; the Company
may assign it to a successor in connection with a merger or a sale of substantially all of its
assets. If any provision is held unenforceable, the remainder continues in effect. This Agreement
may be signed in counterparts, including by electronic signature, each of which is an original.

IN WITNESS WHEREOF, the parties have executed this Agreement as of the Effective Date.


[COMPANY NAME]                            [CONTRACTOR NAME]


By: _______________________________       By: _______________________________

Name: _____________________________       Name: _____________________________

Title: ____________________________       Title: ____________________________

Date: _____________________________       Date: _____________________________


─────────────────────────────────────────────────────────────────────────────
SCHEDULE A — DELIVERABLES AND PAYMENT STAGES
─────────────────────────────────────────────────────────────────────────────
List each deliverable as a file, not as an outcome. Include quantity, format, dimensions, colour
build and the source, working and print-ready files that go with each item. Then set out the payment
stages, keeping a meaningful final stage against delivery of the file package in Section 5.

Deliverables:

[DELIVERABLES]

_____________________________________________________________________________

_____________________________________________________________________________

Payment stages:

_____________________________________________________________________________

Contractor initials: __________          Company initials: __________


─────────────────────────────────────────────────────────────────────────────
SCHEDULE B — CONTRACTOR PRIOR MATERIALS
─────────────────────────────────────────────────────────────────────────────
Everything the Contractor owned or created before this Project, or wholly independently of it, that
is carved out of the assignment in Section 2. Anything not listed here is Work Product and belongs
to the Company. Write "None" if there is none.

[PRIOR MATERIALS]

_____________________________________________________________________________

_____________________________________________________________________________

Contractor initials: __________          Company initials: __________


─────────────────────────────────────────────────────────────────────────────
SCHEDULE C — THIRD-PARTY MATERIAL AND LICENCES
─────────────────────────────────────────────────────────────────────────────
Every third-party font, image, illustration, music track, sound effect, plug-in, template or model
inside or needed for the Deliverables. For each one: what it is, the licence, who holds it, and
whether it reaches packaging, products for resale, retailer listings and paid media. Write "None" if
there is none.

[THIRD PARTY MATERIALS]

_____________________________________________________________________________

_____________________________________________________________________________

Contractor initials: __________          Company initials: __________
`,
  },
];

export default PEOPLE_TEMPLATES;
